import React, { useState } from 'react';
import { XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
type ModalType = 'terms' | 'privacy' | null;
export function Footer() {
  const currentYear = new Date().getFullYear();
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-charcoal text-white/60 py-12 text-center text-sm" data-id="element-40">
        <div className="max-w-4xl mx-auto px-6 space-y-4" data-id="element-41">
          <p className="font-serif text-xl text-white/80 mb-6" data-id="element-42">
            El Kit de Primeros Auxilios Emocionales
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-8" data-id="element-43">
            <button onClick={() => setActiveModal('terms')} className="hover:text-white transition-colors cursor-pointer" data-id="element-44">
              Términos y Condiciones
            </button>
            <button onClick={() => setActiveModal('privacy')} className="hover:text-white transition-colors cursor-pointer" data-id="element-45">
              Política de Privacidad
            </button>
          </div>
          <p data-id="element-46">© {currentYear} Todos los derechos reservados.</p>
          <p className="text-xs mt-4 max-w-2xl mx-auto opacity-50" data-id="element-47">
            Este producto no sustituye el consejo médico profesional,
            diagnóstico o tratamiento. Si usted o su hijo están experimentando
            una crisis de salud mental, por favor contacte a un profesional
            inmediatamente.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-48">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-49">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-50">
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100" data-id="element-51">
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-52">
                  {activeModal === 'terms' ? 'Términos y Condiciones' : 'Política de Privacidad'}
                </h2>
                <button onClick={() => setActiveModal(null)} className="p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-800" aria-label="Cerrar" data-id="element-53">
                  <XIcon className="w-5 h-5" data-id="element-54" />
                </button>
              </div>

              <div className="px-6 py-5 overflow-y-auto max-h-[calc(80vh-64px)] text-gray-700 text-sm leading-relaxed space-y-4" data-id="element-55">
                {activeModal === 'terms' ? <TermsContent data-id="element-56" /> : <PrivacyContent data-id="element-57" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-xs text-gray-400" data-id="element-58">Última actualización: marzo 2026</p>

      <h3 className="font-semibold text-gray-900 text-base mt-2" data-id="element-59">
        1. Aceptación de los Términos
      </h3>
      <p data-id="element-60">
        Al acceder y utilizar este sitio web y adquirir el producto "El Kit de
        Primeros Auxilios Emocionales", usted acepta cumplir con estos Términos
        y Condiciones. Si no está de acuerdo con alguno de estos términos, le
        pedimos que no utilice nuestros servicios.
      </p>

      <h3 className="font-semibold text-gray-900 text-base" data-id="element-61">
        2. Descripción del Producto
      </h3>
      <p data-id="element-62">
        "El Kit de Primeros Auxilios Emocionales" es un producto digital
        educativo que proporciona herramientas y recursos para el bienestar
        emocional. Este producto es de carácter informativo y educativo, y{' '}
        <strong data-id="element-63">no sustituye</strong> el consejo médico profesional, diagnóstico
        o tratamiento de salud mental.
      </p>

      <h3 className="font-semibold text-gray-900 text-base" data-id="element-64">
        3. Uso del Producto
      </h3>
      <p data-id="element-65">
        El contenido proporcionado es para uso personal y no comercial. Queda
        prohibida la reproducción, distribución, modificación o reventa del
        material sin autorización expresa por escrito.
      </p>

      <h3 className="font-semibold text-gray-900 text-base" data-id="element-66">
        4. Política de Reembolso
      </h3>
      <p data-id="element-67">
        Ofrecemos una garantía de satisfacción. Si no está satisfecho con el
        producto, puede solicitar un reembolso dentro de los 30 días posteriores
        a la compra, siempre y cuando se cumplan las condiciones establecidas en
        nuestra política de reembolso.
      </p>

      <h3 className="font-semibold text-gray-900 text-base" data-id="element-68">
        5. Limitación de Responsabilidad
      </h3>
      <p data-id="element-69">
        No nos hacemos responsables de los resultados individuales obtenidos con
        el uso de este producto. Los resultados pueden variar según cada
        persona. En ningún caso seremos responsables por daños directos,
        indirectos, incidentales o consecuentes derivados del uso de este
        producto.
      </p>

      <h3 className="font-semibold text-gray-900 text-base" data-id="element-70">
        6. Propiedad Intelectual
      </h3>
      <p data-id="element-71">
        Todo el contenido, incluyendo textos, gráficos, logotipos, imágenes y
        material descargable, es propiedad exclusiva del creador y está
        protegido por las leyes de propiedad intelectual aplicables.
      </p>

      <h3 className="font-semibold text-gray-900 text-base" data-id="element-72">
        7. Modificaciones
      </h3>
      <p data-id="element-73">
        Nos reservamos el derecho de modificar estos Términos y Condiciones en
        cualquier momento. Las modificaciones entrarán en vigor inmediatamente
        después de su publicación en este sitio web. El uso continuado del
        servicio constituye la aceptación de los términos modificados.
      </p>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-xs text-gray-400" data-id="element-74">Última actualización: marzo 2026</p>

      <h3 className="font-semibold text-gray-900 text-base mt-2" data-id="element-75">
        1. Información que Recopilamos
      </h3>
      <p data-id="element-76">
        Recopilamos únicamente la información necesaria para procesar su compra
        y entregarle el producto digital. Esto puede incluir:
      </p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-77">
        <li data-id="element-78">Nombre completo</li>
        <li data-id="element-79">Dirección de correo electrónico</li>
        <li data-id="element-80">Información de pago (procesada de forma segura por terceros)</li>
      </ul>

      <h3 className="font-semibold text-gray-900 text-base" data-id="element-81">
        2. Uso de la Información
      </h3>
      <p data-id="element-82">Utilizamos su información personal para:</p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-83">
        <li data-id="element-84">Procesar y entregar su compra</li>
        <li data-id="element-85">Enviar comunicaciones relacionadas con el producto</li>
        <li data-id="element-86">Mejorar nuestros servicios y experiencia del usuario</li>
        <li data-id="element-87">Cumplir con obligaciones legales</li>
      </ul>

      <h3 className="font-semibold text-gray-900 text-base" data-id="element-88">
        3. Protección de Datos
      </h3>
      <p data-id="element-89">
        Implementamos medidas de seguridad técnicas y organizativas para
        proteger su información personal contra acceso no autorizado,
        alteración, divulgación o destrucción. Los pagos son procesados a través
        de plataformas seguras de terceros y no almacenamos datos de tarjetas de
        crédito.
      </p>

      <h3 className="font-semibold text-gray-900 text-base" data-id="element-90">
        4. Cookies y Tecnologías de Seguimiento
      </h3>
      <p data-id="element-91">
        Nuestro sitio web puede utilizar cookies y tecnologías similares para
        mejorar la experiencia del usuario y analizar el tráfico del sitio.
        Puede configurar su navegador para rechazar cookies, aunque esto podría
        afectar la funcionalidad del sitio.
      </p>

      <h3 className="font-semibold text-gray-900 text-base" data-id="element-92">
        5. Compartir Información con Terceros
      </h3>
      <p data-id="element-93">
        No vendemos, intercambiamos ni transferimos su información personal a
        terceros, excepto cuando sea necesario para procesar pagos, cumplir con
        la ley o proteger nuestros derechos.
      </p>

      <h3 className="font-semibold text-gray-900 text-base" data-id="element-94">6. Sus Derechos</h3>
      <p data-id="element-95">Usted tiene derecho a:</p>
      <ul className="list-disc pl-5 space-y-1" data-id="element-96">
        <li data-id="element-97">Acceder a sus datos personales</li>
        <li data-id="element-98">Solicitar la corrección de datos inexactos</li>
        <li data-id="element-99">Solicitar la eliminación de sus datos</li>
        <li data-id="element-100">Retirar su consentimiento en cualquier momento</li>
        <li data-id="element-101">Darse de baja de comunicaciones de marketing</li>
      </ul>

      <h3 className="font-semibold text-gray-900 text-base" data-id="element-102">
        7. Cambios en esta Política
      </h3>
      <p data-id="element-103">
        Nos reservamos el derecho de actualizar esta Política de Privacidad en
        cualquier momento. Cualquier cambio será publicado en esta página con la
        fecha de actualización correspondiente.
      </p>
    </>;
}